// "¿Es el mismo producto? ¿Los unimos?" — ADR-2026-09-29-importacion-masiva-duplicados.
// Cuando la importación trae algo que parece un producto que la marca ya tenía, Atlas SUGIERE
// unirlos; nunca los une solo. Unir: lo que ataba al importado con Mercado Libre o Tiendanube pasa al producto que ya
// estaba (y el importado se borra si nadie lo usó). "No son lo mismo": se descarta.

import React from "react";
import { createPortal } from "react-dom";
import { ArrowLeftRight, Loader2, Package, X } from "lucide-react";
import {
  descartarRepetido,
  sugerenciasDeRepetidos,
  unirRepetido,
  type ProductoDeSugerencia,
  type SugerenciaDeRepetido,
} from "../lib/importaciones";

const Miniatura: React.FC<{ p: ProductoDeSugerencia; etiqueta: string }> = ({
  p,
  etiqueta,
}) => (
  <div className="min-w-0 flex-1">
    <div className="aspect-square w-full overflow-hidden rounded-lg bg-neutral-100">
      {p.foto ? (
        <img
          src={p.foto}
          alt={p.nombre ?? ""}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-brand-black/25">
          <Package className="h-5 w-5" strokeWidth={1.5} />
        </div>
      )}
    </div>
    <div className="mt-1.5 text-[11px] uppercase tracking-wide text-brand-black/40">
      {etiqueta}
    </div>
    <div className="text-[13px] leading-snug text-brand-black/80 line-clamp-2">
      {p.nombre ?? "Sin nombre"}
    </div>
  </div>
);

/**
 * Los repetidos sugeridos de la marca. Se vuelven a preguntar cada vez que cambia `refrescar`
 * (avanza o termina una importación). Los usa el botón de refresh de Mis productos: el puntito y
 * la línea de su panel.
 */
/** "De Tiendanube", "De Mercado Libre" o, si no se sabe, "Importado". */
function etiquetaDeOrigen(origen: SugerenciaDeRepetido["nuevo"]["origen"]): string {
  if (origen === "tiendanube") return "De Tiendanube";
  if (origen === "mercadolibre") return "De Mercado Libre";
  return "Importado";
}

export function useRepetidosDeImportacion(brandId: string | null, refrescar: string) {
  const [lista, setLista] = React.useState<SugerenciaDeRepetido[]>([]);
  const marcaRef = React.useRef(brandId);
  marcaRef.current = brandId;
  React.useEffect(() => {
    setLista([]);
    const marca = brandId;
    void sugerenciasDeRepetidos(marca).then((l) => {
      if (marcaRef.current === marca) setLista(l);
    });
  }, [brandId, refrescar]);
  const quitar = React.useCallback(
    (id: string) => setLista((l) => l.filter((x) => x.id !== id)),
    [],
  );
  return { lista, quitar };
}

/** El modal "¿Son el mismo producto?" con la lista que le pasan. */
export const RepetidosDeImportacion: React.FC<{
  brandId: string | null;
  lista: SugerenciaDeRepetido[];
  abierto: boolean;
  onCerrar: () => void;
  /** Se unió o descartó uno: sale de la lista y la grilla trae lo que cambió. */
  onResuelto: (id: string) => void;
}> = ({ brandId, lista, abierto, onCerrar, onResuelto }) => {
  const [trabajando, setTrabajando] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [aviso, setAviso] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (abierto) {
      setError(null);
      setAviso(null);
    }
  }, [abierto]);

  if (!abierto) return null;

  const resolver = async (
    s: SugerenciaDeRepetido,
    accion: "unir" | "descartar",
  ) => {
    if (trabajando) return;
    setTrabajando(s.id);
    setError(null);
    setAviso(null);
    try {
      if (accion === "unir") {
        const r = await unirRepetido(s.id, brandId);
        setAviso(
          r.borrado
            ? "Listo: quedó todo en tu producto de Atlas."
            : "Listo: quedó todo en tu producto de Atlas. El que se importó ya lo usaste, así que no lo borramos.",
        );
      } else {
        await descartarRepetido(s.id, brandId);
      }
      onResuelto(s.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo. Probá de nuevo.");
    } finally {
      setTrabajando(null);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 z-[80]">
      <div className="bg-surface-card rounded-lg shadow-lg border border-neutral-200 max-w-md w-full max-h-[85vh] flex flex-col p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] animate-modal-appear">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">
            ¿Son el mismo producto?
          </h3>
          <button
            type="button"
            onClick={() => !trabajando && onCerrar()}
            className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="mb-4 text-[13px] text-neutral-500">
          Si lo son, unilos: lo que vino de la tienda queda en tu
          producto de Atlas y no queda repetido.
        </p>
        {aviso && (
          <p className="mb-3 text-[13px] text-emerald-700">{aviso}</p>
        )}
        {error && (
          <p className="mb-3 text-[13px] text-red-600">{error}</p>
        )}
        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {lista.length === 0 && (
            <p className="text-[13px] text-neutral-500">
              No quedan productos para revisar.
            </p>
          )}
          {lista.map((s) => (
            <div
              key={s.id}
              className="rounded-xl border border-black/[0.07] p-3"
            >
              <div className="flex items-start gap-3">
                <Miniatura p={s.nuevo} etiqueta={etiquetaDeOrigen(s.nuevo.origen)} />
                <ArrowLeftRight className="mt-10 h-4 w-4 shrink-0 text-brand-black/30" />
                <Miniatura
                  p={s.existente}
                  etiqueta="Ya estaba en Atlas"
                />
              </div>
              {s.motivo && (
                <div className="mt-2 text-[12px] text-brand-black/45">
                  {s.motivo}
                </div>
              )}
              <div className="mt-3 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => resolver(s, "descartar")}
                  disabled={!!trabajando}
                  className="h-[var(--btn-h)] px-4 inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors disabled:opacity-50"
                >
                  No son lo mismo
                </button>
                <button
                  type="button"
                  onClick={() => resolver(s, "unir")}
                  disabled={!!trabajando}
                  className="h-[var(--btn-h)] px-4 inline-flex items-center justify-center gap-1.5 rounded-md shadow-sm font-medium text-[length:var(--text-body)] transition-colors bg-brand-primary hover:bg-brand-primary-hover text-white disabled:opacity-60"
                >
                  {trabajando === s.id && (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                  Unir
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default RepetidosDeImportacion;
